import { createContext, useContext } from "react";
import type { GeolocationState } from "@/hooks/useGeolocation";
import type { MyVisit } from "@/lib/visits";
import { getFloorState, subscribeFloor, type FloorStateMap } from "@/services/floorBus";
import { selectSignedIn, useAuthStore } from "@/services/authStore";
import type { TickerEvent } from "@/types/events";
import type { Discount, Restaurant, RestaurantId } from "@/types/restaurant";

/**
 * The `/showcase-cluj` sales demo (docs/features/showcase.md) renders the real
 * Home + Welcome pages inside this context. Every shared hook that would reach
 * a server (feed fetch, floor WebSocket, visits, AI ask, analytics) checks it
 * and serves the demo's local data instead. `null` everywhere else, where each
 * check is a no-op, so the real app is untouched.
 */

/** Where live floor state comes from: the real WebSocket bus, or the demo simulator. */
export interface FloorSource {
  subscribe: (listener: (state: FloorStateMap) => void) => () => void;
  getState: () => FloorStateMap;
}

export const REAL_FLOOR: FloorSource = { subscribe: subscribeFloor, getState: getFloorState };

export interface Showcase {
  /** Route root of the demo (the city gate); the feed lives at `${basePath}/cluj`. */
  basePath: string;
  cityId: string;
  restaurants: Restaurant[];
  floor: FloorSource;
  /** A fixed "you are here" fix (no permission prompt, ever). */
  geo: GeolocationState;
  /** The demo persona's visit history, so "Recommended for you" is personalised. */
  myVisits: MyVisit[];
  tickerEvents: TickerEvent[];
  /** The demo's real offers and events (`pages/showcase/data/offers.json`), as feed rows. */
  discountsByRestaurant: Record<RestaurantId, Discount[]>;
  /** Simulation speed (1, 2, 5 or 10×) and the speed button's cycle; the ticker is unaffected. */
  speed: number;
  cycleSpeed: () => void;
}

export const ShowcaseContext = createContext<Showcase | null>(null);

/** Route root of the demo. Never linked from the app; disallowed in robots.txt. */
export const SHOWCASE_BASE = "/showcase-cluj";

/** True when the page was loaded on the demo (read before the router mounts). */
export function isShowcasePath(): boolean {
  return typeof window !== "undefined" && window.location.pathname.startsWith(SHOWCASE_BASE);
}

export function useShowcase(): Showcase | null {
  return useContext(ShowcaseContext);
}

/** The demo renders the signed-in view (live capacity, Offers unlocked) without an account. */
export function useViewerSignedIn(): boolean {
  const signedIn = useAuthStore(selectSignedIn);
  return useShowcase() !== null || signedIn;
}

export function useFloorSource(): FloorSource {
  return useShowcase()?.floor ?? REAL_FLOOR;
}
